/**
 * A session, as the SPA addresses it. The eve agent persists each user's
 * sessions; this module lists them and replays a session's prior events so the
 * chat can be reopened and continued, carries the operations that act on one
 * session (title it, cancel its turn, delete it), and classifies the two turn
 * failures that are terminal rather than retryable, so what a failure means to a
 * session is decided in one place.
 *
 * Same-origin via the Worker proxy (`/api/eve/v1/*`); the staff bearer is
 * attached server-side and the session cookie rides along. History is
 * best-effort UI: a 401 or any failure resolves to "no history" rather than
 * throwing into the chat shell.
 *
 * NOTE: we deliberately do NOT import `Client` from `eve/client` here. That
 * entry transitively pulls in eve's Node-only build/runtime modules (they call
 * `createRequire` from `node:module`), which Vite externalizes for the browser
 * — leaving `createRequire` undefined and white-screening the app on boot. The
 * type is erased at build time, so a type-only import is safe; the session
 * stream is a plain NDJSON GET we replay ourselves (see `fetchConversationEvents`).
 */
import type { ClientError, HandleMessageStreamEvent } from 'eve/client';

export interface Conversation {
  sessionId: string;
  /** Agent-assigned title; null until the session has been summarized. */
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConversationsResponse {
  conversations: Conversation[];
}

/**
 * Lists the signed-in user's conversations, newest activity first is not
 * guaranteed by the API; callers group/sort by `updatedAt`. Returns `[]` on
 * 401 (signed out) or any error.
 */
export async function listConversations(): Promise<Conversation[]> {
  try {
    const res = await fetch('/api/eve/v1/conversations', { credentials: 'same-origin' });
    if (!res.ok) return [];
    const body = (await res.json()) as ConversationsResponse;
    return Array.isArray(body?.conversations) ? body.conversations : [];
  } catch {
    return [];
  }
}

/**
 * Titles a conversation from the user's first question. The server Haiku-
 * summarizes the prompt (see the eve `…/title` route) and stores it; the title
 * is always question-derived, never the assistant's answer. Best-effort: a
 * failure just leaves the conversation untitled ("Conversation" in the sidebar).
 */
export async function setConversationTitle(sessionId: string, prompt: string): Promise<void> {
  try {
    await fetch(`/api/eve/v1/conversations/${encodeURIComponent(sessionId)}/title`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ prompt }),
    });
  } catch {
    // best-effort; the sidebar falls back to "Conversation"
  }
}

/**
 * Requests cooperative cancellation of a session's in-flight turn (eve 0.25+).
 * eve aborts the durable server-side run and settles the turn as `turn.cancelled`
 * (not a failure), so the stream ends, the UI leaves its streaming state, and
 * the partial answer is kept.
 *
 * `turnId` names the turn to stop. Unscoped, a cancel applies to whatever turn is
 * active when it ARRIVES, so a turn ending on its own mid-flight leaves it to hit
 * whatever runs next. Named, eve drops a cancel whose id does not match the
 * running turn — so pass the id of the turn being stopped, and omit it only
 * before eve has named that turn, where "whatever is active" is both the sole
 * option and the right target.
 *
 * Delivery only. eve answers 202 when it hands the cancel to the run and 200 when
 * there is no active turn (including an expired or unknown session), and neither
 * says the run has actually STOPPED — which is the whole reason the caller also
 * bounds its wait. A failure is logged rather than returned for the same reason:
 * it would report transport, and the caller needs to speak about the turn.
 */
export async function cancelTurn(sessionId: string, turnId?: string): Promise<void> {
  try {
    const res = await fetch(`/api/eve/v1/session/${encodeURIComponent(sessionId)}/cancel`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      // Truthiness, not a null check: eve 400s a zero-length turnId.
      body: JSON.stringify(turnId ? { turnId } : {}),
    });
    if (!res.ok) console.warn('[cancelTurn] cancel not delivered', res.status);
  } catch (err) {
    console.warn('[cancelTurn] cancel never reached eve', err);
  }
}

/**
 * The user's first question in a replayed thread. Used to backfill a title for
 * an untitled conversation when it's opened — its first message is already in
 * the events we fetch to restore it, so no extra request is needed. `null` if
 * the thread has no user message yet.
 */
export function firstUserMessage(events: HandleMessageStreamEvent[]): string | null {
  for (const e of events) {
    if (e.type === 'message.received') {
      const m = e.data.message.trim();
      if (m) return m;
    }
  }
  return null;
}

/** eve's error code for a follow-up sent to a session that is no longer live. */
const SESSION_NOT_ACTIVE = 'session_not_active';

/**
 * eve's error code for a session whose inbox is not accepting messages. eve's
 * client retries a `send` that gets it for about 20 seconds before surfacing it
 * (a `respond` surfaces it at once). One that outlasts that retry is almost
 * always a session no deployment can take a message for, such as one a runtime
 * upgrade left on a run the new runtime cannot resume. A deploy or compaction
 * handoff slower than the retry reads the same, and there the person continues
 * in a new chat although the old one would have recovered.
 */
const SESSION_NOT_READY = 'session_not_ready';

/** The codes this SPA treats as a session that takes no more messages. */
const CLOSED_SESSION_CODES: ReadonlySet<ClientError['code']> = new Set([SESSION_NOT_ACTIVE, SESSION_NOT_READY]);

/**
 * Whether a send failed because the session is closed, rather than because the
 * request never landed. eve retires a session on an explicit reset, at its
 * lifetime deadline, and when a runtime upgrade leaves an older run
 * unresumable; a follow-up to a retired session answers 409 with
 * `code: "session_not_active"`, and one whose inbox does not come back answers
 * 409 `session_not_ready` (see that constant for what it can also mean). The
 * distinction is the whole point: a transport failure is worth retrying and a
 * closed session never will be, so callers offer a fresh chat instead of
 * inviting a retry that cannot succeed. The thread stays readable either way,
 * since replay is a separate read path.
 */
export function isSessionClosed(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  // Read through eve's own `ClientError['code']`, as `isProtocolRejection` reads
  // `status`; eve types the code as a string, so the values themselves are not
  // pinned by the type.
  const code = (err as { code?: ClientError['code'] }).code;
  return CLOSED_SESSION_CODES.has(code);
}

/** HTTP status eve answers a request body it cannot parse with. */
const BAD_REQUEST = 400;

/**
 * Whether a send failed because eve refused the request itself, rather than
 * because the request never landed. eve validates the message body and answers a
 * shape it does not accept with 400 on its `ClientError`.
 *
 * A tab keeps the JavaScript it loaded for as long as it stays open, so a
 * long-lived tab runs its original bundle through every later deploy. The SPA and
 * eve share a wire protocol, and every field this SPA sends is one eve accepts, so
 * a 400 means the two sides no longer agree on that protocol and this tab is
 * holding the older half of it. Only a reload can fix that, which is why it is
 * classified apart from the retryable transport error, same as a closed session is.
 *
 * **An empty message reaches the same 400**, so callers must refuse one rather
 * than let it be reported as staleness (see `ChatView.send`). A change that moved
 * a route rather than a body field would 404 instead and is not caught here,
 * because a 404 is also how a genuinely missing session reads.
 */
export function isProtocolRejection(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  // Read through eve's own `ClientError['status']` rather than a local `number`.
  // What breaks this classifier is an eve upgrade moving that field, and typing it
  // from eve makes that a typecheck failure here: a renamed field fails to index,
  // and a retyped one leaves nothing overlapping `BAD_REQUEST` to compare. A test
  // over a hand-made `{ status: 400 }` asserts this file against itself and stays
  // green through exactly the change worth catching.
  return (err as { status?: ClientError['status'] }).status === BAD_REQUEST;
}

/**
 * Deletes one of the signed-in user's conversations (server-scoped to the
 * caller, so only their own). Returns `true` on success. The caller updates the
 * list optimistically and can re-sync on `false`.
 */
export async function deleteConversation(sessionId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/eve/v1/conversations/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Replays a session's prior stream events so they can seed `useEveAgent`'s
 * `initialEvents`. `useEveAgent` does not auto-replay a resumed session, so the
 * full thread must be fetched here. Returns `[]` on error (the conversation
 * then opens empty rather than crashing).
 *
 * This mirrors what eve's `Client.session(id).stream({ startIndex: 0 })` does
 * on the wire — a GET to the session stream route returning newline-delimited
 * JSON events — without importing the Node-poisoned client (see the file note).
 */
export async function fetchConversationEvents(sessionId: string): Promise<HandleMessageStreamEvent[]> {
  const events: HandleMessageStreamEvent[] = [];
  // The stream replays the recorded backlog (a fast flush) and then, for a
  // parked session, stays OPEN to tail future events. We only want the backlog,
  // so abort once the stream goes quiet: otherwise restore waits for the open
  // connection to close (which can take a very long time). The backlog arrives
  // in a burst well under this window, so an idle gap means it's drained.
  const controller = new AbortController();
  let idle: ReturnType<typeof setTimeout> | undefined;
  const armIdle = () => {
    clearTimeout(idle);
    idle = setTimeout(() => controller.abort(), 1000);
  };
  try {
    const res = await fetch(`/api/eve/v1/session/${encodeURIComponent(sessionId)}/stream`, {
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!res.ok || !res.body) return events;

    armIdle();
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    const flush = (chunk: string) => {
      buf += chunk;
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        if (line) {
          try {
            events.push(JSON.parse(line) as HandleMessageStreamEvent);
          } catch {
            // Skip a malformed line rather than abandoning the whole replay.
          }
        }
      }
    };
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      armIdle();
      flush(decoder.decode(value, { stream: true }));
    }
    return events;
  } catch {
    // AbortError (idle drain) or any network error: return what we collected.
    // The backlog flushes before the idle window, so an abort returns the full
    // recorded thread.
    return events;
  } finally {
    clearTimeout(idle);
  }
}
